/**
 * Client-side diagnostic ring buffers for the "Report an issue" widget.
 *
 * Captures (capped, FIFO):
 *  - console.error / console.warn calls
 *  - window 'error' (window.onerror) + 'unhandledrejection' events
 *  - FAILED network/API calls — fed by the axios interceptor via recordNetworkFailure()
 *
 * Everything is scrubbed of secrets/PII before it enters a buffer (scrub.ts). Buffers are bounded
 * so they never grow without limit. install() is idempotent and safe to call once at app start.
 */

import { scrubText, scrubUrl } from '@/lib/issue-reporting/scrub';
import type { CapturedConsoleError, CapturedNetworkError, ClientContext } from '@/types/issue-reports';

const MAX_CONSOLE = 30;
const MAX_NETWORK = 30;

const consoleBuffer: CapturedConsoleError[] = [];
const networkBuffer: CapturedNetworkError[] = [];

function push<T>(buffer: T[], item: T, cap: number): void {
  buffer.push(item);
  if (buffer.length > cap) {
    buffer.splice(0, buffer.length - cap);
  }
}

function nowIso(): string {
  return new Date().toISOString();
}

/** Record a console-level diagnostic. Message + source are scrubbed. */
export function recordConsoleError(level: 'error' | 'warn', message: string, source?: string): void {
  const entry: CapturedConsoleError = source
    ? { at: nowIso(), level, message: scrubText(message), source: scrubText(source) }
    : { at: nowIso(), level, message: scrubText(message) };
  push(consoleBuffer, entry, MAX_CONSOLE);
}

/** Record a FAILED network/API call. Called from the axios response interceptor. URL is scrubbed. */
export function recordNetworkFailure(input: {
  readonly method: string;
  readonly url: string;
  readonly status: number;
  readonly statusText?: string;
  readonly durationMs?: number;
}): void {
  const entry: CapturedNetworkError = {
    at: nowIso(),
    method: input.method.toUpperCase(),
    url: scrubUrl(input.url),
    status: input.status,
    ...(input.statusText !== undefined ? { statusText: scrubText(input.statusText) } : {}),
    ...(input.durationMs !== undefined ? { durationMs: Math.round(input.durationMs) } : {}),
  };
  push(networkBuffer, entry, MAX_NETWORK);
}

/** Snapshot copies of the current buffers (newest last). */
export function getConsoleErrors(): readonly CapturedConsoleError[] {
  return consoleBuffer.slice();
}
export function getNetworkErrors(): readonly CapturedNetworkError[] {
  return networkBuffer.slice();
}

/** TEST helper — empty both buffers. */
export function _clearBuffersForTests(): void {
  consoleBuffer.length = 0;
  networkBuffer.length = 0;
}

function argsToMessage(args: readonly unknown[]): string {
  return args
    .map((a) => {
      if (a instanceof Error) return `${a.name}: ${a.message}`;
      if (typeof a === 'string') return a;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(' ');
}

let installed = false;

/**
 * Hook console.error/warn + global error events into the buffers. Idempotent.
 * Returns an uninstall function (used by tests; in production we install once for the app's life).
 */
export function installDebugCapture(): () => void {
  if (installed || typeof window === 'undefined') {
    return () => {
      /* no-op */
    };
  }
  installed = true;

  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);

  console.error = (...args: unknown[]): void => {
    recordConsoleError('error', argsToMessage(args));
    originalError(...args);
  };
  console.warn = (...args: unknown[]): void => {
    recordConsoleError('warn', argsToMessage(args));
    originalWarn(...args);
  };

  const onError = (event: ErrorEvent): void => {
    const msg = event.error instanceof Error ? `${event.error.name}: ${event.error.message}` : event.message;
    const source = event.filename ? `${event.filename}:${String(event.lineno)}:${String(event.colno)}` : undefined;
    recordConsoleError('error', msg, source);
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    const reason: unknown = event.reason;
    const msg =
      reason instanceof Error ? `Unhandled rejection: ${reason.name}: ${reason.message}` : `Unhandled rejection: ${argsToMessage([reason])}`;
    recordConsoleError('error', msg);
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    console.error = originalError;
    console.warn = originalWarn;
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    installed = false;
  };
}

// ── Client context ────────────────────────────────────────────────────────────

/** Best-effort parse of browser name+version from a UA string. */
function parseBrowser(ua: string): string {
  const tests: readonly (readonly [RegExp, string])[] = [
    [/Edg\/([\d.]+)/, 'Edge'],
    [/OPR\/([\d.]+)/, 'Opera'],
    [/Chrome\/([\d.]+)/, 'Chrome'],
    [/Firefox\/([\d.]+)/, 'Firefox'],
    [/Version\/([\d.]+).*Safari/, 'Safari'],
  ];
  for (const [re, name] of tests) {
    const m = re.exec(ua);
    if (m) return `${name} ${m[1] ?? ''}`.trim();
  }
  return 'Unknown';
}

/** Best-effort parse of OS name from a UA string. */
function parseOperatingSystem(ua: string): string {
  if (ua.includes('Windows NT 10')) return 'Windows 10/11';
  if (ua.includes('Windows')) return 'Windows';
  if (/Mac OS X ([\d_]+)/.test(ua)) return `macOS ${(/Mac OS X ([\d_]+)/.exec(ua)?.[1] ?? '').replace(/_/g, '.')}`.trim();
  if (/Android ([\d.]+)/.test(ua)) return `Android ${/Android ([\d.]+)/.exec(ua)?.[1] ?? ''}`.trim();
  if (/(iPhone|iPad).*OS ([\d_]+)/.test(ua)) return `iOS ${(/OS ([\d_]+)/.exec(ua)?.[1] ?? '').replace(/_/g, '.')}`.trim();
  if (ua.includes('Linux')) return 'Linux';
  return 'Unknown';
}

/**
 * Collect the auto-captured client context. `routePath` defaults to the current pathname; callers
 * inside the router can pass the resolved route to be precise.
 */
export function collectClientContext(routePath?: string): ClientContext {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return {
    pageUrl: scrubUrl(typeof window !== 'undefined' ? window.location.href : ''),
    routePath: routePath ?? (typeof window !== 'undefined' ? window.location.pathname : ''),
    userAgent: ua,
    browser: parseBrowser(ua),
    operatingSystem: parseOperatingSystem(ua),
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
    clientReportedAtUtc: nowIso(),
  };
}
